import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Chart from '../lib/chartTheme';
import { fetchReport, pct, type DifotReport, type Filters } from '../lib/difot';
import { BrandMark } from '../components/BrandMark';
import '../tv.css';

// A wall-display view: chrome-less, large type, auto-refreshing.
// Curated to TODAY's operations only — the headline DIFOT KPIs plus a doughnut
// of the day's delivery outcomes. Runs off the normal logged-in session
// (gated by ProtectedRoute).

const REFRESH_MS = 5 * 60 * 1000; // re-pull the report every 5 minutes
const TV_FILTERS: Filters = { range: 'today', regions: [], drivers: [], status: '' };

export function Tv() {
  const [report, setReport] = useState<DifotReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      // Locked to the live business day: TV_FILTERS uses range 'today', which
      // resolves to the real current calendar day (Australia/Sydney). Stops are
      // matched on trip_date = today, so the wallboard only ever shows the live
      // day's operations.
      const rep = await fetchReport(TV_FILTERS);
      setReport(rep);
      setUpdatedAt(new Date());
      setError(null);
    } catch (err) {
      console.error('TV report load failed', err);
      setError(err instanceof Error ? err.message : 'Failed to load the report');
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const id = window.setInterval(() => void load(), REFRESH_MS);
    return () => window.clearInterval(id);
  }, [load]);

  return (
    <div className="tv">
      <TvHeader updatedAt={updatedAt} refreshing={refreshing} />

      {error && !report && (
        <div className="tv-error" role="alert">
          Could not load the DIFOT report: {error}
        </div>
      )}

      {report && (
        <div className="tv-body">
          <Hero report={report} />
          <section className="tv-card tv-today">
            <div className="tv-card-title">Today's Delivery Outcomes</div>
            <OutcomesDonut report={report} />
          </section>
        </div>
      )}

      {!report && !error && <div className="tv-loading">Loading DIFOT report…</div>}
    </div>
  );
}

// ─── Header: brand + live clock + freshness + controls ──────────────────────

function TvHeader({ updatedAt, refreshing }: { updatedAt: Date | null; refreshing: boolean }) {
  const [now, setNow] = useState(() => new Date());
  const [fs, setFs] = useState(false);

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const onChange = () => setFs(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void document.documentElement.requestFullscreen().catch(() => undefined);
  };

  const clock = now.toLocaleTimeString('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Australia/Sydney',
  });
  const day = now.toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'Australia/Sydney',
  });

  return (
    <header className="tv-head">
      <div className="tv-brand">
        <BrandMark className="tv-logo" size={64} />
        <div>
          <div className="tv-eyebrow">WARRRL · Powered by Locate2u</div>
          <h1>DIFOT · Today</h1>
        </div>
      </div>
      <div className="tv-head-right">
        <div className="tv-clock">
          <div className="tv-time num">{clock}</div>
          <div className="tv-day">{day}</div>
        </div>
        <div className="tv-fresh">
          <span className={`tv-dot ${refreshing ? 'busy' : 'live'}`} />
          {refreshing
            ? 'Refreshing…'
            : updatedAt
              ? `Updated ${updatedAt.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Sydney' })}`
              : 'Live'}
        </div>
        <div className="tv-controls">
          <button type="button" onClick={toggleFullscreen} title="Toggle fullscreen">
            {fs ? 'Exit full screen' : 'Full screen'}
          </button>
          <Link to="/difot" className="tv-exit" title="Back to dashboard">
            Exit
          </Link>
        </div>
      </div>
    </header>
  );
}

// ─── Hero: giant DIFOT % + secondary KPI strip ──────────────────────────────

function Hero({ report }: { report: DifotReport }) {
  const t = report.totals;
  const difot = pct(t.completed, t.denom);
  const tone = difot >= 95 ? 'good' : difot >= 90 ? 'warn' : 'bad';

  const kpis = [
    {
      label: 'On Time',
      value: `${pct(t.in_window_n, t.completed_with_window).toFixed(1)}%`,
      sub: `${t.completed_with_window.toLocaleString()} with a window`,
      color: '#13a874',
    },
    { label: 'Failed', value: t.failed.toLocaleString(), sub: `${pct(t.failed, t.denom).toFixed(1)}% of attempted`, color: '#d9412f' },
    { label: 'Pending', value: t.pending.toLocaleString(), sub: `${pct(t.pending, t.denom).toFixed(1)}% of attempted`, color: '#8a7a6b' },
    // Cancelled is excluded from `denom`, so its sub-line is a share of all stops.
    { label: 'Cancelled', value: t.cancelled.toLocaleString(), sub: `${pct(t.cancelled, t.total).toFixed(1)}% of ${t.total.toLocaleString()} stops`, color: '#626866' },
    { label: 'Active Drivers', value: t.drivers.toLocaleString(), sub: t.avg_service != null ? `${t.avg_service.toFixed(1)}m avg service` : 'avg service —', color: '#2c2e2d' },
  ];

  return (
    <section className="tv-hero">
      <div className={`tv-hero-main tone-${tone}`}>
        <div className="tv-hero-label">DIFOT</div>
        <div className="tv-hero-value num">{difot.toFixed(1)}%</div>
        <div className="tv-hero-sub">
          {t.completed.toLocaleString()} of {t.denom.toLocaleString()} attempted delivered
        </div>
      </div>
      <div className="tv-kpis">
        {kpis.map((k) => (
          <div className="tv-kpi" key={k.label}>
            <div className="tv-kpi-accent" style={{ background: k.color }} />
            <div className="tv-kpi-label">{k.label}</div>
            <div className="tv-kpi-value num" style={{ color: k.color }}>
              {k.value}
            </div>
            <div className="tv-kpi-sub">{k.sub}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Today's outcomes doughnut + legend ─────────────────────────────────────

const OUTCOME_COLOURS = {
  delivered: '#13a874',
  failed: '#d9412f',
  pending: '#8a7a6b',
  cancelled: '#c9d6d0',
} as const;

function OutcomesDonut({ report }: { report: DifotReport }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart | null>(null);
  const t = report.totals;

  const segments = useMemo(
    () =>
      [
        { label: 'Delivered', value: t.completed, color: OUTCOME_COLOURS.delivered },
        { label: 'Failed', value: t.failed, color: OUTCOME_COLOURS.failed },
        { label: 'Pending', value: t.pending, color: OUTCOME_COLOURS.pending },
        { label: 'Cancelled', value: t.cancelled, color: OUTCOME_COLOURS.cancelled },
      ].filter((s) => s.value > 0),
    [t.completed, t.failed, t.pending, t.cancelled],
  );

  useEffect(() => {
    if (!canvasRef.current) return;
    chartRef.current?.destroy();
    chartRef.current = new Chart(canvasRef.current, {
      type: 'doughnut',
      data: {
        labels: segments.map((s) => s.label),
        datasets: [
          {
            data: segments.map((s) => s.value),
            backgroundColor: segments.map((s) => s.color),
            borderColor: '#ffffff',
            borderWidth: 3,
            hoverOffset: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        cutout: '64%',
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
      },
    });
    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [segments]);

  if (t.total === 0) {
    return <div className="tv-empty">No stops scheduled for today yet</div>;
  }

  return (
    <div className="tv-today-body">
      <div className="tv-donut-wrap">
        <canvas ref={canvasRef} />
        <div className="tv-donut-center">
          <div className="tv-donut-total num">{t.total.toLocaleString()}</div>
          <div className="tv-donut-cap">stops today</div>
        </div>
      </div>
      <div className="tv-legend">
        {segments.map((s) => (
          <div className="tv-legend-row" key={s.label}>
            <span className="tv-legend-dot" style={{ background: s.color }} />
            <span className="tv-legend-label">{s.label}</span>
            <span className="tv-legend-count num">{s.value.toLocaleString()}</span>
            <span className="tv-legend-pct num">{pct(s.value, t.total).toFixed(0)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}
